import React, { useEffect, useState } from 'react'
import { PageTitle } from '../root/components/common'
import classNames from 'classnames'
import { makeStyles } from '@material-ui/core'
import { useDispatch, useSelector } from 'react-redux'
import { getBillingDataQuery } from './api'
import { useQuery } from 'react-query'
import moment from 'moment'
import { capitalizeFirstLetter } from '../../utils'
import { enqueueSnackbar } from '../root/actions'
import { ExternalUsersBillingTable } from './external-users-billing-table'
import { EscalationLevelsBillingTable } from './escalation-levels-billing-table'
import { DevicesApiBillingTable } from './devices-api-billing-table'

/* eslint-disable */
export default function Billing({ embedded, preselectedBillingData }) {
    const dispatch = useDispatch()
    const classes = useStyles()
    const { theme, locale } = useSelector(state => state.main)

    const [selectedBilling, setSelectedBilling] = useState(null)
    const [billingExternalUsers, setBillingExternalUsers] = useState(null)
    const [billingEscalationLevels, setBillingEscalationLevels] = useState(null)
    const [billingApiDevices, setBillingApiDevices] = useState(null)

    const { data: billingData, isLoading } = useQuery(
        ['getBillingData'],
        () => getBillingDataQuery(),
        {
            refetchOnWindowFocus: false,
            refetchInterval: false,
            enabled: !embedded && !preselectedBillingData,
            onError: () => {
                dispatch(
                    enqueueSnackbar({
                        message: locale.sagasMsgs.commonLoadingError,
                        options: {
                            variant: 'error'
                        }
                    })
                )
            }
        }
    )

    const selectBillingMonth = billingItem => {
        setSelectedBilling(billingItem)
        setBillingExternalUsers(null)
        setBillingEscalationLevels(null)
        setBillingApiDevices(null)
    }

    const selectBillingExternalUsers = billingExternalUsers => {
        if (billingExternalUsers?.length === 0) return
        setBillingExternalUsers(billingExternalUsers)
        setBillingEscalationLevels(null)
        setBillingApiDevices(null)
    }

    const selectBillingEscalationLevels = billingEscalationLevels => {
        if (billingEscalationLevels?.length === 0) return
        setBillingExternalUsers(null)
        setBillingEscalationLevels(billingEscalationLevels)
        setBillingApiDevices(null)
    }

    const selectBillingApiDevices = billingApiDevices => {
        if (billingApiDevices?.length === 0) return
        setBillingExternalUsers(null)
        setBillingEscalationLevels(null)
        setBillingApiDevices(billingApiDevices)
    }

    useEffect(() => {
        selectBillingMonth(null)
    }, [embedded, preselectedBillingData])

    return (
        <div className={classNames(classes.wrapper)}>
            {!embedded && !preselectedBillingData ? (
                <PageTitle
                    title={locale.billing.billing}
                    className={classes.pageTitle}
                ></PageTitle>
            ) : null}
            <div className={classes.content}>
                <div
                    style={{
                        width: 240,
                        paddingRight: 20,
                        overflowY: 'auto'
                    }}
                >
                    {(embedded && preselectedBillingData
                        ? preselectedBillingData
                        : billingData
                    )?.map(bItem => {
                        return (
                            <div
                                key={`${bItem.month}-${bItem.year}`}
                                className={classNames(classes.hoverOpacity)}
                                onClick={() => selectBillingMonth(bItem)}
                                style={{
                                    display: 'flex',
                                    padding: 20,
                                    borderRadius: theme.borderRadius,
                                    backgroundColor:
                                        `${bItem.month}-${bItem.year}` ===
                                        `${selectedBilling?.month}-${selectedBilling?.year}`
                                            ? theme.primary
                                            : theme.tertiary,
                                    cursor: 'pointer',
                                    marginBottom: 10,
                                    boxShadow:
                                        '6px 6px 9px -3px rgba(0,0,0,0.15)',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    position: 'relative',
                                    color:
                                        `${bItem.month}-${bItem.year}` ===
                                        `${selectedBilling?.month}-${selectedBilling?.year}`
                                            ? theme.textPrimaryReverted
                                            : theme.textPrimary
                                }}
                            >
                                {bItem.is_current ? (
                                    <div
                                        style={{
                                            position: 'absolute',
                                            width: 6,
                                            height: 6,
                                            borderRadius: '50%',
                                            backgroundColor: theme.cta,
                                            top: 8,
                                            right: 8
                                        }}
                                    ></div>
                                ) : null}
                                <div>
                                    {capitalizeFirstLetter(
                                        moment(bItem.month + 1, 'MM').format(
                                            'MMMM'
                                        )
                                    )}
                                    &nbsp;
                                    {bItem.year}
                                </div>
                                <div
                                    style={{
                                        fontWeight: 'bold',
                                        fontSize: 18,
                                        textAlign: 'right'
                                    }}
                                >
                                    &nbsp;
                                    {bItem.total
                                        ?.toFixed(2)
                                        .replace(/\.|\,/g, ',')}
                                    €
                                </div>
                            </div>
                        )
                    })}
                </div>
                <div style={{ width: 240, paddingRight: 20 }}>
                    {selectedBilling !== null ? (
                        <>
                            <div
                                className={classNames({
                                    [`${classes.hoverOpacity}`]:
                                        selectedBilling.external_users_amount >
                                        0,
                                    [`${classes.cursorPointer}`]:
                                        selectedBilling.external_users_amount >
                                        0
                                })}
                                onClick={() =>
                                    selectBillingExternalUsers(
                                        selectedBilling.external_users
                                    )
                                }
                                style={{
                                    display: 'flex',
                                    padding: 20,
                                    borderRadius: theme.borderRadius,
                                    backgroundColor: theme.tertiary,
                                    marginBottom: 10,
                                    boxShadow:
                                        '6px 6px 9px -3px rgba(0,0,0,0.15)',
                                    alignItems: 'center',
                                    justifyContent: 'space-between'
                                }}
                            >
                                <div>
                                    <div>{locale.billing.externalUsers}</div>
                                    <div>
                                        {locale.billing.amount}:{' '}
                                        {selectedBilling.external_users_amount}
                                    </div>
                                </div>
                                <div
                                    style={{
                                        fontWeight: 'bold',
                                        fontSize: 18,
                                        textAlign: 'right'
                                    }}
                                >
                                    &nbsp;
                                    {selectedBilling.external_users_total
                                        ?.toFixed(2)
                                        .replace(/\.|\,/g, ',')}
                                    €
                                </div>
                            </div>
                            <div
                                className={classNames({
                                    [`${classes.hoverOpacity}`]:
                                        selectedBilling.escalation_levels_amount >
                                        0,
                                    [`${classes.cursorPointer}`]:
                                        selectedBilling.escalation_levels_amount >
                                        0
                                })}
                                onClick={() =>
                                    selectBillingEscalationLevels(
                                        selectedBilling.escalation_levels
                                    )
                                }
                                style={{
                                    display: 'flex',
                                    padding: 20,
                                    borderRadius: theme.borderRadius,
                                    backgroundColor: theme.tertiary,
                                    marginBottom: 10,
                                    boxShadow:
                                        '6px 6px 9px -3px rgba(0,0,0,0.15)',
                                    alignItems: 'center',
                                    justifyContent: 'space-between'
                                }}
                            >
                                <div>
                                    <div>
                                        {locale.locations.escalationLevels}
                                    </div>
                                    <div>
                                        {locale.billing.amount}:{' '}
                                        {
                                            selectedBilling.escalation_levels_amount
                                        }
                                    </div>
                                </div>
                                <div
                                    style={{
                                        fontWeight: 'bold',
                                        fontSize: 18,
                                        textAlign: 'right'
                                    }}
                                >
                                    &nbsp;
                                    {selectedBilling.escalation_levels_total
                                        ?.toFixed(2)
                                        .replace(/\.|\,/g, ',')}
                                    €
                                </div>
                            </div>
                            <div
                                className={classNames({
                                    [`${classes.hoverOpacity}`]:
                                        selectedBilling.api_devices_amount > 0,
                                    [`${classes.cursorPointer}`]:
                                        selectedBilling.api_devices_amount > 0
                                })}
                                onClick={() =>
                                    selectBillingApiDevices(
                                        selectedBilling.api_devices
                                    )
                                }
                                style={{
                                    display: 'flex',
                                    padding: 20,
                                    borderRadius: theme.borderRadius,
                                    backgroundColor: theme.tertiary,
                                    marginBottom: 10,
                                    boxShadow:
                                        '6px 6px 9px -3px rgba(0,0,0,0.15)',
                                    alignItems: 'center',
                                    justifyContent: 'space-between'
                                }}
                            >
                                <div>
                                    <div>{locale.billing.apiDevices}</div>
                                    <div>
                                        {locale.billing.amount}:{' '}
                                        {selectedBilling.api_devices_amount}
                                    </div>
                                </div>
                                <div
                                    style={{
                                        fontWeight: 'bold',
                                        fontSize: 18,
                                        textAlign: 'right'
                                    }}
                                >
                                    &nbsp;
                                    {selectedBilling.api_devices_total
                                        ?.toFixed(2)
                                        .replace(/\.|\,/g, ',')}
                                    €
                                </div>
                            </div>
                            <div
                                style={{
                                    display: 'flex',
                                    padding: 20,
                                    marginTop: 20,
                                    marginBottom: 10,
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    borderTop: `1px dashed ${theme.primary}`
                                }}
                            >
                                <div
                                    style={{
                                        fontWeight: 'bold',
                                        fontSize: 24
                                    }}
                                >
                                    Total:
                                </div>
                                <div
                                    style={{
                                        fontWeight: 'bold',
                                        fontSize: 24,
                                        textAlign: 'right'
                                    }}
                                >
                                    &nbsp;
                                    {selectedBilling.total
                                        ?.toFixed(2)
                                        .replace(/\.|\,/g, ',')}
                                    €
                                </div>
                            </div>
                        </>
                    ) : null}
                </div>
                <div style={{ flex: 1, height: '100%' }}>
                    {billingExternalUsers !== null &&
                    billingExternalUsers.length > 0 ? (
                        <ExternalUsersBillingTable
                            data={billingExternalUsers}
                        />
                    ) : null}
                    {billingEscalationLevels !== null &&
                    billingEscalationLevels.length > 0 ? (
                        <EscalationLevelsBillingTable
                            data={billingEscalationLevels}
                        />
                    ) : null}
                    {billingApiDevices !== null &&
                    billingApiDevices.length > 0 ? (
                        <DevicesApiBillingTable data={billingApiDevices} />
                    ) : null}
                </div>
            </div>
        </div>
    )
}

const useStyles = makeStyles(theme => ({
    wrapper: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        flex: 1
    },
    pageTitle: {
        justifyContent: 'space-between',
        height: 56
    },
    content: {
        height: 'calc(100% - 56px)',
        display: 'flex'
    },
    hoverOpacity: {
        '&:hover': {
            opacity: 0.75
        }
    },
    cursorPointer: {
        cursor: 'pointer'
    }
}))
/* eslint-enable */
